import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckOutlined from '@ant-design/icons/lib/icons/CheckOutlined';

import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpEmpty } from 'new-components/DLS/OpEmpty/OpEmpty';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';
import { heliumSend } from 'utils/helpers';

import { VisitorWorkflowsState } from '../../../../../store/slices/visitorWorkflowsSlice';
import { VisitsState } from '../../../../../store/slices/visitsSlice';
import { ImmutableRootState } from '../../../../../store/store';
import { Agreement } from '../../../../../types/agreementTypes';
import { DocumentCheck } from '../../../../../types/visitTypes';

interface AgreementsTabProps {
  extendedVisitorInfo: DocumentCheck;
  attendantAgreements: Agreement[];
  kioskAgreements: Agreement[];
  signedAgreements: Agreement[];
}

const AgreementsTab = ({
  extendedVisitorInfo,
  attendantAgreements,
  kioskAgreements,
  signedAgreements,
}: AgreementsTabProps) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const { orgId } = getOpParams();

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const selectedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisitor']),
  ) as VisitsState['selectedVisitor'];
  const selectedSignInWorkflowConfig = useSelectorJs(
    (state: ImmutableRootState) =>
      state.getIn(['visitorWorkflows', 'selectedSignInWorkflowConfig']),
  ) as VisitorWorkflowsState['selectedSignInWorkflowConfig'];

  const handleViewSignedAgreements = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await heliumSend(
        'getVisitAgreementSignedUrl',
        [orgId, selectedVisit?.id, selectedVisitor?.id],
        {},
      );

      if (response.errorMessage) {
        throw new Error(response.errorMessage);
      }

      // New API returns Signed URL
      const agreementData = response.data as {
        url?: string;
        contentType?: string;
        expiresAt?: number;
      } | null;
      const pdfUrl = agreementData?.url;

      if (!pdfUrl) {
        throw new Error('No PDF URL received');
      }

      // Download PDF using Signed URL - fetch blob to force download (cross-origin)
      const pdfResponse = await fetch(pdfUrl);
      if (!pdfResponse.ok) {
        throw new Error('Failed to fetch PDF');
      }
      const blob = await pdfResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'agreements.pdf';
      link.click();

      // Cleanup blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (
        errorMessage.includes('404') ||
        errorMessage.toLowerCase().includes('not found')
      ) {
        notificationService.show(
          'warning',
          t(
            'visitors.profile.notification.agreementNotFoundWarningDescription',
          ),
          t('visitors.profile.notification.agreementNotFoundWarning'),
        );
      }
    } finally {
      setIsDownloading(false);
    }
  };
  const isDisplayEnabled =
    selectedSignInWorkflowConfig?.agreementsDisplay === 1;
  const hasSignedPDF = !!extendedVisitorInfo?.agreement;

  let displayAgreements: Agreement[] = [];

  if (isDisplayEnabled) {
    displayAgreements = [...attendantAgreements, ...signedAgreements];

    if (hasSignedPDF) {
      displayAgreements = [
        ...attendantAgreements,
        ...signedAgreements,
        ...kioskAgreements,
      ];
    }
  } else if (!isDisplayEnabled) {
    displayAgreements = [...signedAgreements];

    if (hasSignedPDF) {
      displayAgreements = [...signedAgreements, ...kioskAgreements];
    }
  }

  // Remove duplicates based on id
  displayAgreements = Array.from(
    new Map(displayAgreements.map((a) => [a.id, a])).values(),
  );

  return (
    <>
      <OpList
        itemLayout="horizontal"
        dataSource={displayAgreements}
        bordered
        locale={{
          emptyText: (
            <OpEmpty description={t('visitors.profile.agreements.noData')} />
          ),
        }}
        renderItem={(item) => {
          const agreement = item as Agreement;
          return (
            <OpList.Item
              extra={
                signedAgreements.some(
                  (signedAgreement) => signedAgreement.id === agreement.id,
                ) && <CheckOutlined style={{ color: 'green' }} />
              }
            >
              <OpList.Item.Meta
                title={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    {agreement.name}
                  </div>
                }
              />
            </OpList.Item>
          );
        }}
      />
      {extendedVisitorInfo?.agreement != null && (
        <OpButton
          onClick={handleViewSignedAgreements}
          type="primary"
          ghost
          loading={isDownloading}
          style={{ width: '100%', marginTop: '10px' }}
        >
          {t('visitors.profile.agreements.viewSigned')}
        </OpButton>
      )}
    </>
  );
};

export default AgreementsTab;
